import { supabase } from "@/lib/supabaseClient";
import type { EmployeeDisputeRow, TimesheetDataset, TimesheetRow } from "./mappers";
import { asErrorMessage, toBoolean, toNumber } from "./mappers";

export async function getTimesheetRowsForPeriod(params: {
  companyId: string;
  periodStart: string;
  periodEnd: string;
}): Promise<TimesheetDataset> {
  const { companyId, periodStart, periodEnd } = params;

  const { data: calcData, error: calcErr } = await supabase
    .from("calculated_overtime_timesheet")
    .select(
      "user_id, company_id, work_date, raw_hours, full_name, source_type, has_shadowing_anomaly, daily_ot, daily_reg, week_start_date, hrs_already_worked_this_week, weekly_ot_carry, integrity_alert"
    )
    .eq("company_id", companyId)
    .gte("work_date", periodStart)
    .lte("work_date", periodEnd)
    .order("work_date", { ascending: true });

  if (calcErr) throw new Error(asErrorMessage(calcErr));

  const { data: sotData, error: sotErr } = await supabase
    .from("source_of_truth_timesheet")
    .select("user_id, company_id, entry_time, project_id, log_id, is_public_holiday, status")
    .eq("company_id", companyId)
    .gte("entry_time", `${periodStart}T00:00:00.000Z`)
    .lte("entry_time", `${periodEnd}T23:59:59.999Z`);

  if (sotErr) throw new Error(asErrorMessage(sotErr));

  const sotMap = new Map<string, any>();

  for (const row of (sotData ?? []) as any[]) {
    const workDate =
      typeof row.entry_time === "string"
        ? row.entry_time.slice(0, 10)
        : "";

    const key = `${row.user_id}|${workDate}`;

    sotMap.set(key, row);
  }

  const rows: TimesheetRow[] = ((calcData ?? []) as any[]).map((row) => {
    const employeeId = String(row.user_id);
    const key = `${employeeId}|${row.work_date}`;
    const sourceRow = sotMap.get(key);

    const projectId =
      sourceRow?.project_id == null ? null : String(sourceRow.project_id);

    return {
      user_id: employeeId,
      company_id: String(row.company_id),
      work_date: String(row.work_date),
      full_name: String(row.full_name ?? "Employee"),
      source_type: String(row.source_type ?? "unknown"),
      has_shadowing_anomaly: toBoolean(row.has_shadowing_anomaly),
      integrity_alert: toBoolean(row.integrity_alert),
      raw_hours: toNumber(row.raw_hours),
      daily_reg: toNumber(row.daily_reg),
      daily_ot: toNumber(row.daily_ot),
      weekly_ot_carry: toNumber(row.weekly_ot_carry),

      project_id: projectId,
      log_id: sourceRow?.log_id ?? null,
      is_public_holiday: sourceRow?.is_public_holiday ?? null,
      status: sourceRow?.status ?? null,
    } as TimesheetRow;
  });

  const [
    { data: disputesData, error: disputesErr },
    { data: unmappedData, error: unmappedErr },
  ] = await Promise.all([
    supabase.rpc("list_company_open_time_disputes_in_range", {
      p_company_id: companyId,
      p_start_date: periodStart,
      p_end_date: periodEnd,
    }),

    supabase.rpc("list_company_unmapped_logs_in_range", {
      p_company_id: companyId,
      p_start_date: periodStart,
      p_end_date: periodEnd,
    }),
  ]);

  if (disputesErr) throw new Error(asErrorMessage(disputesErr));

  if (unmappedErr) throw new Error(asErrorMessage(unmappedErr));

  const disputesCount = (disputesData ?? []).length;
  const unmappedCount = (unmappedData ?? []).length;
  const alerts = rows.filter((row) => row.integrity_alert).length;
  const employees = new Set(rows.map((row) => row.user_id)).size;

  return {
    rows,

    summary: {
      totalDays: rows.length,
      employees,
      alerts,
      openDisputes: disputesCount,
      unmappedLogs: unmappedCount,
    },
  };
}

export async function approveTimesheetRange(params: {
  companyId: string;
  start: string;
  end: string;
}): Promise<void> {
  const { error } = await supabase.rpc("approve_timesheet_range", {
    p_company_id: params.companyId,
    p_start: params.start,
    p_end: params.end,
  });

  if (error) throw new Error(asErrorMessage(error));
}

export async function getEmployeeDisputesForPeriod(params: {
  companyId: string;
  employeeId?: string | null;
  periodStart: string;
  periodEnd: string;
}): Promise<EmployeeDisputeRow[]> {
  const { data, error } = await supabase.rpc(
    "list_company_open_time_disputes_in_range",
    {
      p_company_id: params.companyId,
      p_start_date: params.periodStart,
      p_end_date: params.periodEnd,
    }
  );

  if (error) throw new Error(asErrorMessage(error));

  return ((data ?? []) as any[])
    .filter((row) =>
      params.employeeId
        ? String(row.employee_id) === params.employeeId
        : true
    )
    .map((row) => ({
      id: String(row.id),
      company_id: params.companyId,
      employee_id: String(row.employee_id),
      employee_full_name:
        row.employee_full_name == null
          ? null
          : String(row.employee_full_name),
      employee_custom_id:
        row.employee_custom_id == null
          ? null
          : String(row.employee_custom_id),
      work_date:
        row.work_date == null
          ? null
          : String(row.work_date),
      proposed_hours:
        row.proposed_hours == null
          ? null
          : Number(row.proposed_hours),
      reason:
        row.reason == null
          ? null
          : String(row.reason),
      status: String(row.status),
      created_at: String(row.created_at),
      updated_at: null,
      payload: undefined,
      resolution_note:
        row.manager_feedback == null
          ? null
          : String(row.manager_feedback),
    }));
}

export async function updateTimeDispute(params: {
  disputeId: string;
  companyId: string;
  status: "resolved" | "dismissed";
  resolutionNote: string;
}): Promise<void> {
  const { error } = await supabase.rpc("resolve_employee_time_dispute", {
    p_dispute_id: params.disputeId,
    p_resolution_status: params.status,
    p_resolution_note: params.resolutionNote,
  });

  if (error) throw new Error(asErrorMessage(error));
}

export async function createManualTimeLog(params: {
  companyId: string;
  employeeId: string;
  projectId: string | null;
  entryTime: string;
  exitTime: string | null;
  totalHours: number;
  note?: string | null;
}) {
  const { data, error } = await supabase.rpc("create_manual_time_log", {
    p_company_id: params.companyId,
    p_employee_id: params.employeeId,
    p_project_id: params.projectId,
    p_entry_time: params.entryTime,
    p_exit_time: params.exitTime,
    p_total_hours: params.totalHours,
    p_note: params.note ?? null,
  });

  if (error) throw new Error(asErrorMessage(error));

  if (!data?.ok) {
    throw new Error(String(data?.message ?? "Failed to create manual time log."));
  }

  return data;
}

export async function bulkCreateManualTimeLogs(params: {
  companyId: string;
  rows: {
    employee_id: string;
    project_id: string;
    entry_time: string;
    exit_time?: string | null;
    total_hours: number;
    note?: string | null;
  }[];
}) {
  const { data, error } = await supabase.rpc(
    "bulk_create_manual_time_logs",
    {
      p_company_id: params.companyId,
      p_rows: params.rows,
    }
  );

  if (error) throw new Error(asErrorMessage(error));

  return data;
}

export async function validateTeamLeaderTimesheetRows(params: {
  companyId: string;
  logIds: string[];
  note?: string | null;
}) {
  const { data, error } = await supabase.rpc(
    "validate_team_leader_timesheet_rows",
    {
      p_company_id: params.companyId,
      p_log_ids: params.logIds,
      p_note: params.note ?? null,
    }
  );

  if (error) throw new Error(asErrorMessage(error));

  return data;
}

export async function correctManualTimeLog(params: {
  companyId: string;
  originalLogId: string;
  entryTime: string;
  exitTime: string | null;
  totalHours: number;
  projectId?: string | null;
  reason: string;
}) {
  const { data, error } = await supabase.rpc("correct_timesheet_log", {
    p_company_id: params.companyId,
    p_original_log_id: params.originalLogId,
    p_entry_time: params.entryTime,
    p_exit_time: params.exitTime,
    p_total_hours: params.totalHours,
    p_project_id: params.projectId ?? null,
    p_reason: params.reason,
  });

  if (error) throw new Error(asErrorMessage(error));

  return data;
}